import React, { useMemo, useState } from 'react';
import { cn, formatRelativeDate, formatTime } from '@/app/lib/utils';
import Avatar from '../ui/Avatar';
import Icon from '../ui/Icon';
import {
  User,
  Linkedin,
  Mail,
  SquarePen,
  Globe,
  MailOpen,
  Building,
  MessageCircle,
} from 'lucide-react';
import GradientInfoIcon from '../ui/GradientInfoIcon';
import Pill from '@/app/components/ui/Pill';
import { SignalData, SignalGroup } from '@/types/signals';
import { signalEntityPillProps } from '@/constants/color';
import Tooltip from '../ui/Tooltip';
import { useParams } from 'next/navigation';

import CompanyMiniCard from '../common/CompanyMiniCard';
import ContactMiniCard from '../common/ContactMiniCard';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import { getTargetPages } from '@/api/target-pages';

const signalEntityLucideIcons: Record<string, React.ElementType> = {
  linkedin: Linkedin,
  email: Mail,
  form: SquarePen,
  page: Globe,
  response: MailOpen,
  persona: User,
  icp: Building,
  imessage: MessageCircle,
};

const stripHTML = (html: string) => {
  if (!html) return '';
  return html
    .replace(/<[^>]*>?/gm, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const withProtocol = (url: string) => {
  const value = url.trim();
  if (!value) return '';
  if (value.startsWith('http://') || value.startsWith('https://')) return value;
  if (value.startsWith('/')) return `https://example.com${value}`;
  return `https://${value}`;
};

const getUrlParts = (url?: string | null) => {
  if (!url) return null;

  try {
    const parsed = new URL(withProtocol(url));
    const host = parsed.hostname.replace(/^www\./, '').toLowerCase();
    const pathname = parsed.pathname.replace(/\/$/, '') || '/';
    const search = parsed.search;

    return {
      host,
      path: `${pathname}${search}`.toLowerCase(),
      full: `${host}${pathname === '/' ? '' : pathname}${search}`.toLowerCase(),
    };
  } catch {
    const cleaned = url
      .trim()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/\/$/, '')
      .toLowerCase();

    return {
      host: '',
      path: cleaned.startsWith('/') ? cleaned : '',
      full: cleaned,
    };
  }
};

const getPageDisplayUrl = (url?: string | null) => {
  const parts = getUrlParts(url);
  if (!parts) return '';

  return parts.full || url || '';
};

const formatTimeSpent = (seconds?: number) => {
  if (!seconds || seconds <= 0) return '';

  const wholeSeconds = Math.round(seconds);
  const minutes = Math.floor(wholeSeconds / 60);
  const remainingSeconds = wholeSeconds % 60;

  if (minutes > 0 && remainingSeconds > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  }
  if (minutes > 0) {
    return `${minutes}m`;
  }
  return `${remainingSeconds}s`;
};

const getSignalActivityTimeText = (signal: SignalData, showFullDate: boolean) => {
  const timeSpentText = formatTimeSpent(signal.page?.time_spent);
  const signalTimeText = showFullDate
    ? formatRelativeDate(signal.signal_at)
    : formatTime(signal.signal_at);

  return `${signalTimeText}${timeSpentText ? ` - ${timeSpentText}` : ''}`;
};

const getPageUrlKey = (url?: string | null) => getPageDisplayUrl(url) || url || '-';

const groupPageSignalsByUrl = (signals: SignalData[]) => {
  return signals.reduce<Array<{ url: string; signals: SignalData[] }>>((groups, signal) => {
    const url = getPageUrlKey(signal.page?.url);
    const existing = groups.find(group => group.url === url);

    if (existing) {
      existing.signals.push(signal);
    } else {
      groups.push({ url, signals: [signal] });
    }

    return groups;
  }, []);
};

interface SignalActivityItemProps {
  activity?: SignalData;
  group?: SignalGroup;
  wrapperClassName?: string;
  showContactTooltip?: boolean;
  company?: {
    name: string;
    logo: string;
    linkedin: string;
    qualification_reason?: string;
  };
  showFullDate?: boolean;
}

const SignalActivityItem = React.memo(
  ({
    activity,
    group,
    wrapperClassName,
    showContactTooltip,
    company,
    showFullDate = false,
  }: SignalActivityItemProps) => {
    const { clientId } = useParams();
    const resolvedClientId = Array.isArray(clientId) ? clientId[0] : clientId;
    const t = useTranslations('signals');
    const [isExpanded, setIsExpanded] = useState(false);
    const [selectedSignalId, setSelectedSignalId] = useState<string | null>(null);

    const actualActivity = useMemo(() => {
      if (activity) return activity;
      const firstSignal = group?.signals[0];
      if (selectedSignalId && group) {
        return group.signals.find(s => s.id === selectedSignalId) || firstSignal;
      }
      return firstSignal;
    }, [activity, group, selectedSignalId]);

    const apiTargetPageId = actualActivity?.page?.target_page_id;
    const apiTargetPageShortName = actualActivity?.page?.target_page_short_name;
    const shouldResolveTargetPage =
      actualActivity?.entity === 'page' && !!apiTargetPageId && !apiTargetPageShortName;

    const { data: targetPagesData } = useQuery({
      queryKey: ['signal-target-pages', resolvedClientId],
      queryFn: () =>
        getTargetPages({
          clientId: resolvedClientId || '',
          limit: 500,
        }),
      enabled: !!resolvedClientId && shouldResolveTargetPage,
      staleTime: 5 * 60 * 1000,
    });

    const resolvedTargetPageShortName = useMemo(() => {
      if (apiTargetPageShortName) return apiTargetPageShortName;
      if (!apiTargetPageId) return '';

      return (
        targetPagesData?.data.find(targetPage => targetPage.id === apiTargetPageId)?.short_name ||
        ''
      );
    }, [apiTargetPageId, apiTargetPageShortName, targetPagesData?.data]);

    const emailData = useMemo(() => {
      if (
        actualActivity?.entity !== 'email' ||
        !actualActivity?.message ||
        typeof actualActivity.message !== 'object'
      ) {
        return null;
      }
      const msg = actualActivity.message as any;
      const subject = msg.title || msg.subject || '';
      const body = stripHTML(msg.body || '');
      return { subject, body };
    }, [actualActivity?.entity, actualActivity?.message]);

    const rawMessageText = useMemo(() => {
      if (!actualActivity?.message) return '';

      const entity = actualActivity.entity?.toLowerCase();
      const isEmailEntity = entity === 'email';
      const isLinkedInEntity = entity === 'linkedin';

      if (typeof actualActivity.message === 'string') {
        return actualActivity.message;
      }

      if (typeof actualActivity.message === 'object') {
        const content =
          (actualActivity.message as any).title || (actualActivity.message as any).subject || '';
        if (isEmailEntity) return content || '';
        if (isLinkedInEntity) return content || (actualActivity.message as any).sender?.name || '';
        return content;
      }
      return '';
    }, [actualActivity?.message, actualActivity?.entity]);

    const isIcp = !!actualActivity?.icp_id;
    const pageDisplayUrl = getPageDisplayUrl(actualActivity?.page?.url);
    const messageText = useMemo(() => {
      if (rawMessageText) return rawMessageText;
      const entity = actualActivity?.entity?.toLowerCase();
      if (entity === 'persona' && actualActivity?.contact?.persona_short_name) {
        return `Persona-${actualActivity.contact.persona_short_name}`;
      }
      if (entity === 'icp' && (actualActivity?.icp_short_name || isIcp)) {
        return `ICP-${actualActivity.icp_short_name}`;
      }
      return '';
    }, [
      rawMessageText,
      actualActivity?.entity,
      actualActivity?.contact,
      actualActivity?.icp_short_name,
      isIcp,
    ]);

    const isPageVisited = group?.entity === 'page' && group?.type === 'visited';
    const isEmailOpened = group?.entity === 'email' && group?.type === 'opened';
    const isExpandable = !!group && group.total > 1;

    const displayInfo = useMemo(() => {
      const pageText = pageDisplayUrl || actualActivity?.page?.url || '';
      const text = messageText;
      const entity = actualActivity?.entity?.toLowerCase();
      const action = actualActivity?.action?.toLowerCase();

      if (entity === 'page') {
        return { prefix: t('pageVisits'), text: pageText };
      }
      if (entity === 'form') {
        const prefix = action ? t(`formAction.${action}` as any) : '';
        return { prefix, text };
      }
      if (entity === 'icp' || entity === 'persona') {
        return { prefix: t('evaluated'), text };
      }
      if (entity === 'linkedin' && action === 'connected') {
        const senderName =
          actualActivity?.message && typeof actualActivity.message === 'object'
            ? (actualActivity.message as any).sender?.name
            : '';
        return { prefix: t('connected'), text: senderName };
      }
      if (entity === 'email' || isEmailOpened) {
        return { prefix: `${t('sub')}:`, text: emailData?.subject || text };
      }
      return { prefix: '', text };
    }, [
      messageText,
      pageDisplayUrl,
      actualActivity?.page?.url,
      actualActivity?.entity,
      actualActivity?.action,
      actualActivity?.message,
      isEmailOpened,
      emailData?.subject,
      t,
    ]);

    if (!actualActivity) return null;

    const currentEntity = group?.entity || actualActivity.entity;
    const currentAction = group?.type || actualActivity.action;

    const isIcpSignal = actualActivity.entity === 'icp';
    const isPageSignal = actualActivity.entity === 'page';
    const isFormSignal = actualActivity.entity === 'form';
    const isEmail = actualActivity.entity === 'email';
    const isLinkedIn = actualActivity.entity === 'linkedin';
    const targetPageLabel =
      isPageSignal && resolvedTargetPageShortName
        ? `Target Page - ${resolvedTargetPageShortName}`
        : '';

    const companyName = group?.company_name || company?.name;
    const companyLogo = company?.logo;
    const companyLinkedIn = company?.linkedin;
    const contactLinkedIn = actualActivity.contact?.linkedin;
    const isMultiContactPageGroup =
      isPageSignal && !!group?.contact_name && !group?.contact_id && group.total > 1;
    const hasContact =
      !isMultiContactPageGroup &&
      !!actualActivity.contact &&
      !!(
        actualActivity.contact.id ||
        actualActivity.contact.first_name ||
        actualActivity.contact.last_name ||
        actualActivity.contact.photo_url
      );

    const displayName = group?.contact_name
      ? group.contact_name
      : isIcpSignal
        ? companyName || actualActivity.icp_short_name || '-'
        : hasContact
          ? `${actualActivity.contact?.first_name || ''} ${actualActivity.contact?.last_name || ''}`.trim()
          : companyName || actualActivity.icp_short_name || '-';

    const displayImage = hasContact ? actualActivity.contact?.photo_url : companyLogo;
    const useRoundedAvatar = !hasContact;

    const displayText = `${displayInfo.prefix} ${displayInfo.text}`.trim();
    const activityTimeText = getSignalActivityTimeText(actualActivity, showFullDate);
    const pageUrlGroups = group && isPageVisited ? groupPageSignalsByUrl(group.signals) : [];

    const messageId =
      typeof actualActivity.message === 'object' ? actualActivity.message?.id : undefined;

    const ItemContent = (
      <div className={cn('flex flex-col group outline-none gap-2', wrapperClassName)}>
        <div className="flex items-center justify-between gap-1 w-full overflow-hidden">
          <div
            className={cn(
              'flex items-center overflow-hidden',
              showContactTooltip ? 'gap-1' : 'gap-2'
            )}
          >
            <Avatar
              name={displayName}
              src={displayImage}
              size={20}
              initialsClassName="text-[9px]"
              className={useRoundedAvatar ? 'rounded' : 'rounded-full'}
              imageClassName={useRoundedAvatar ? 'rounded' : 'rounded-full'}
            />
            <div
              className={cn(
                'flex items-center gap-1 flex-1 min-w-0 min-h-[22px] font-medium text-[14px] overflow-hidden text-ellipsis',
                showContactTooltip ? 'gap-1' : 'gap-2'
              )}
            >
              <span className="whitespace-nowrap overflow-hidden text-ellipsis">{displayName}</span>
              {useRoundedAvatar && companyLinkedIn ? (
                <div
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    const url =
                      companyLinkedIn?.startsWith('http') || companyLinkedIn?.startsWith('https')
                        ? companyLinkedIn
                        : `https://${companyLinkedIn}`;
                    window.open(url, '_blank');
                  }}
                  className="hover:underline text-[#0077B5] inline-flex items-center cursor-pointer pointer-events-auto"
                >
                  <Icon name="linkedin-filled" size={4} />
                </div>
              ) : contactLinkedIn ? (
                <div
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    const url =
                      contactLinkedIn?.startsWith('http') || contactLinkedIn?.startsWith('https')
                        ? contactLinkedIn
                        : `https://${contactLinkedIn}`;
                    window.open(url, '_blank');
                  }}
                  className="hover:underline text-[#0077B5] inline-flex items-center cursor-pointer pointer-events-auto"
                >
                  <Icon name="linkedin-filled" size={4} />
                </div>
              ) : null}
              {!showContactTooltip &&
                hasContact &&
                actualActivity.contact?.persona_short_name &&
                actualActivity.contact?.persona_id && (
                  <Pill variant="soft" color="info">
                    <User size={12} strokeWidth={1.5} />
                    {actualActivity.contact.persona_short_name}
                  </Pill>
                )}
              {actualActivity.contact?.qualification_reason && (
                <Tooltip
                  side="right"
                  align="center"
                  delayDuration={0}
                  disableHoverableContent={true}
                  sideOffset={2}
                  className="bg-[#0284c7] text-white px-3 py-[6px] text-xs rounded-[6px] max-w-[300px]"
                  arrowClassName="fill-[#0284c7]"
                  label={actualActivity.contact.qualification_reason}
                >
                  <div className="w-4 h-4 flex items-center">
                    <GradientInfoIcon size={16} />
                  </div>
                </Tooltip>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {(() => {
              if (targetPageLabel) {
                return (
                  <Pill variant="outline" color="purple" rounded={false}>
                    {targetPageLabel}
                  </Pill>
                );
              }

              const pillProps = signalEntityPillProps[currentEntity] || {
                variant: 'soft' as const,
                color: 'neutral' as const,
              };
              const EntityIcon = signalEntityLucideIcons[currentEntity];
              return (
                <Pill
                  {...pillProps}
                  className={cn(
                    ['icp', 'persona'].includes(currentEntity) ? 'uppercase' : 'capitalize'
                  )}
                >
                  {pillProps.variant !== 'outline' && EntityIcon && (
                    <EntityIcon size={12} strokeWidth={1.5} />
                  )}
                  {currentEntity}
                </Pill>
              );
            })()}
          </div>
        </div>

        {group && isPageVisited && group.total > 1 ? (
          <div className="flex flex-col gap-2">
            {pageUrlGroups.map(pageUrlGroup => {
              const isUrlExpandable = pageUrlGroup.signals.length > 1;
              const urlTimeSpentText = formatTimeSpent(
                pageUrlGroup.signals.reduce(
                  (total, signal) => total + (signal.page?.time_spent || 0),
                  0
                )
              );

              if (!isUrlExpandable) {
                const signal = pageUrlGroup.signals[0];

                return (
                  <div
                    key={pageUrlGroup.url}
                    className="flex items-center justify-between gap-1 w-full text-muted-foreground text-[11px]"
                  >
                    {signal.page?.url ? (
                      <div className="flex items-center gap-1 min-w-0 flex-1 overflow-hidden">
                        <span className="font-bold whitespace-nowrap">{t('pageVisits')} </span>
                        <a
                          href={
                            signal.page.url.startsWith('http')
                              ? signal.page.url
                              : `https://${signal.page.url}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-muted-foreground text-[11px] hover:underline transition-colors truncate flex-1 min-w-0"
                          onClick={e => e.stopPropagation()}
                          title={signal.page.url}
                        >
                          {pageUrlGroup.url}
                        </a>
                      </div>
                    ) : (
                      <span className="truncate flex-1" title={pageUrlGroup.url}>
                        <span className="font-bold">{t('pageVisits')} </span>
                        {pageUrlGroup.url}
                      </span>
                    )}
                    <span className="whitespace-nowrap">
                      {getSignalActivityTimeText(signal, showFullDate)}
                    </span>
                  </div>
                );
              }

              return (
                <div key={pageUrlGroup.url} className="bg-secondary rounded py-2">
                  <div
                    className={cn(
                      'flex items-center gap-2 border-border px-2',
                      isUrlExpandable ? 'cursor-pointer' : 'cursor-default',
                      isExpanded ? 'mb-2 border-b border-border pb-2' : ''
                    )}
                    onClick={() => {
                      if (isUrlExpandable) {
                        setIsExpanded(!isExpanded);
                      }
                    }}
                  >
                    {isUrlExpandable && (
                      <Icon
                        name={'arrow-down-s-line'}
                        size={4}
                        className={cn(
                          'text-muted-foreground transition-transform',
                          isExpanded ? 'rotate-0' : '-rotate-90'
                        )}
                      />
                    )}
                    <span className="text-xs font-medium text-muted-foreground">
                      {t('pageVisits')}
                    </span>
                    <Pill color="info" isBadge>
                      {pageUrlGroup.signals.length}
                    </Pill>
                    {urlTimeSpentText && (
                      <div className="rounded-full bg-border px-1.5 h-[14px] flex items-center justify-center text-[11px] font-medium">
                        {urlTimeSpentText}
                      </div>
                    )}
                  </div>
                  {(isExpanded || !isUrlExpandable) && (
                    <div className={cn('flex flex-col gap-2 px-2')}>
                      {pageUrlGroup.signals.map(signal => (
                        <div
                          key={signal.id}
                          className="flex items-center justify-between gap-1 text-[11px] rounded transition-colors"
                        >
                          {signal.page?.url ? (
                            <a
                              href={
                                signal.page.url.startsWith('http')
                                  ? signal.page.url
                                  : `https://${signal.page.url}`
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              className="truncate flex-1 min-w-0 hover:underline transition-colors"
                              title={signal.page.url}
                              onClick={e => e.stopPropagation()}
                            >
                              {pageUrlGroup.url}
                            </a>
                          ) : (
                            <span className="truncate flex-1 text-muted-foreground">
                              {pageUrlGroup.url}
                            </span>
                          )}
                          <span className="whitespace-nowrap">
                            {getSignalActivityTimeText(signal, showFullDate)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : group && isEmailOpened && group.total > 1 ? (
          <div className="bg-secondary rounded py-2">
            <div
              className={cn(
                'flex items-center gap-2 border-border px-2',
                isExpandable ? 'cursor-pointer' : 'cursor-default',
                isExpanded ? 'mb-2 border-b border-border pb-2' : ''
              )}
              onClick={() => {
                if (isExpandable) {
                  setIsExpanded(!isExpanded);
                }
              }}
            >
              {isExpandable && (
                <Icon
                  name={'arrow-down-s-line'}
                  size={4}
                  className={cn(
                    'text-muted-foreground transition-transform',
                    isExpanded ? 'rotate-0' : '-rotate-90'
                  )}
                />
              )}
              <span className="text-xs font-medium text-muted-foreground">{t('emailOpened')}</span>
              <Pill color="info" isBadge>
                {group.total}
              </Pill>
            </div>
            {(isExpanded || !isExpandable) && (
              <div className={cn('flex flex-col gap-2 px-2')}>
                {group.signals.map(signal => (
                  <div
                    key={signal.id}
                    className="flex items-center justify-between gap-1 text-[11px] rounded transition-colors"
                    onMouseEnter={() => {
                      setSelectedSignalId(signal.id);
                    }}
                  >
                    <div
                      className={cn(
                        'flex items-center gap-1 cursor-pointer transition-colors text-muted-foreground overflow-hidden',
                        selectedSignalId === signal.id ||
                          (!selectedSignalId && group.signals[0].id === signal.id)
                          ? 'font-bold'
                          : 'hover:font-bold'
                      )}
                    >
                      <span className="text-xs font-medium">{t('sub')}: </span>
                      <span className="truncate flex-1">
                        {signal.message && typeof signal.message === 'object'
                          ? (signal.message as any).title || (signal.message as any).subject
                          : '-'}
                      </span>
                    </div>
                    <span className="whitespace-nowrap">
                      {getSignalActivityTimeText(signal, showFullDate)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            {isEmail && emailData && (
              <div className="flex flex-col gap-0.5 w-full text-[11px]">
                <div className="flex items-center justify-between gap-1 w-full">
                  <span className="truncate flex-1" title={emailData.subject}>
                    <span className="font-bold">{t('sub')}: </span>
                    {emailData.subject}
                  </span>
                  <span className="text-muted-foreground font-normal">{activityTimeText}</span>
                </div>
              </div>
            )}
            {!isEmail && displayText && (
              <div className="flex items-center justify-between gap-1 w-full text-muted-foreground text-[11px]">
                {isPageSignal && actualActivity.page?.url ? (
                  <div className="flex items-center gap-1 min-w-0 flex-1 overflow-hidden">
                    {displayInfo.prefix && (
                      <span className="font-bold whitespace-nowrap">{displayInfo.prefix} </span>
                    )}
                    <a
                      href={
                        actualActivity.page.url.startsWith('http')
                          ? actualActivity.page.url
                          : `https://${actualActivity.page.url}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-muted-foreground text-[11px] hover:underline transition-colors truncate flex-1 min-w-0"
                      onClick={e => e.stopPropagation()}
                      title={displayText}
                    >
                      {displayInfo.text}
                    </a>
                  </div>
                ) : (
                  <span className="truncate flex-1 min-w-0" title={displayText}>
                    {displayInfo.prefix && <span className="font-bold">{displayInfo.prefix} </span>}
                    {displayInfo.text}
                  </span>
                )}
                <span className="whitespace-nowrap">{activityTimeText}</span>
              </div>
            )}
            {!isEmail && !displayText && (
              <div className="flex justify-end w-full text-muted-foreground text-[11px] whitespace-nowrap">
                {activityTimeText}
              </div>
            )}
          </>
        )}
      </div>
    );

    if (showContactTooltip && !isPageSignal && !isFormSignal) {
      return (
        <Tooltip
          side="left"
          align="center"
          delayDuration={0}
          disableHoverableContent={false}
          persistOnClick={true}
          sideOffset={7}
          className="bg-background p-3 rounded-md shadow-xl border border-border z-50 pointer-events-auto group max-w-[500px]"
          arrowClassName="fill-soft-200"
          label={
            <div className="flex flex-col gap-3 min-w-[280px]">
              {actualActivity.contact ? (
                <div className="flex flex-col gap-2">
                  <ContactMiniCard
                    contact={{
                      id: actualActivity.contact.id,
                      name: displayName,
                      profile_pic: actualActivity.contact.photo_url,
                      linkedin: contactLinkedIn,
                      persona: {
                        id: actualActivity.contact.persona_id,
                        short_name: actualActivity.contact.persona_short_name,
                      },
                    }}
                    showArrow={true}
                    alwaysShowArrow={true}
                    clickable={false}
                    contactTitleClass={isLinkedIn && companyName ? 'underline' : ''}
                  />
                  {actualActivity.entity === 'persona' &&
                    actualActivity.contact.qualification_reason && (
                      <div className="bg-primary/10 rounded p-3 flex items-center gap-2 items-start mt-1">
                        <Icon name="mattic-ai" size={4} className="text-foreground mt-0.5" />
                        <span className="text-[12px] text-foreground leading-tight">
                          {actualActivity.contact.qualification_reason}
                        </span>
                      </div>
                    )}
                </div>
              ) : isPageSignal || isIcpSignal ? (
                <div className="flex flex-col gap-1">
                  <CompanyMiniCard
                    company={{
                      id: actualActivity.company_id || '',
                      name: companyName || '',
                      logo: companyLogo || '',
                      linkedin: companyLinkedIn,
                    }}
                  />
                  {actualActivity.entity === 'icp' && company?.qualification_reason && (
                    <div className="bg-primary/10 rounded p-3 flex items-center gap-2 items-start mt-1">
                      <Icon name="mattic-ai" size={4} className="text-foreground mt-0.5" />
                      <span className="text-[12px] text-foreground leading-tight">
                        {company.qualification_reason}
                      </span>
                    </div>
                  )}
                </div>
              ) : null}

              {isLinkedIn && (
                <div className="flex flex-col gap-2">
                  {typeof actualActivity.message === 'object' &&
                    actualActivity.message?.sender?.name && (
                      <div className="flex justify-between items-center gap-2">
                        <span className="text-xs text-muted-foreground font-medium font-inter">
                          {t('connected')}
                        </span>
                        <div className="flex items-center gap-1.5">
                          <Avatar
                            name={actualActivity.message.sender.name}
                            src={actualActivity.message.sender.profile_pic_url}
                            size={16}
                            initialsClassName="text-[7px]"
                          />
                          <span className="text-xs font-medium text-foreground truncate max-w-[120px]">
                            {actualActivity.message.sender.name}
                          </span>
                        </div>
                      </div>
                    )}
                  <div className="flex justify-between items-center gap-2">
                    <span className="text-xs text-muted-foreground font-medium font-inter">
                      Status
                    </span>
                    {(() => {
                      const pillProps = signalEntityPillProps[currentEntity] || {
                        variant: 'soft' as const,
                        color: 'neutral' as const,
                      };
                      const EntityIcon = signalEntityLucideIcons[currentEntity];
                      return (
                        <Pill {...pillProps}>
                          {pillProps.variant !== 'outline' && EntityIcon && (
                            <EntityIcon size={12} strokeWidth={1.5} />
                          )}
                          <span className="capitalize font-medium">
                            {isLinkedIn ? currentAction : currentEntity}
                          </span>
                        </Pill>
                      );
                    })()}
                  </div>
                </div>
              )}

              {isEmail && messageId && (
                <>
                  {emailData && (
                    <div className="bg-secondary rounded p-2 text-xs text-muted-foreground mt-1">
                      <div className="flex flex-col gap-1.5 max-w-[400px]">
                        <span className="font-medium text-foreground">
                          {t('sub')}: {emailData.subject}
                        </span>
                        {emailData.body && <span className="line-clamp-3">{emailData.body}</span>}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          }
        >
          {ItemContent}
        </Tooltip>
      );
    }

    return ItemContent;
  }
);

SignalActivityItem.displayName = 'SignalActivityItem';
export default SignalActivityItem;
